import { runInAction } from 'mobx';
import { observer, useLocalStore } from 'mobx-react';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ERROR_CODES, WLIDS } from 'Constants';
import { appConfigStore } from 'Models/AppConfigStore';
import { UserEntity } from 'Models/Entities';
import { store } from 'Models/Store';
import { guestCreation } from 'Services/Api/AuthorizationService';
import { checkIfInvalidName } from 'Util/StringUtils';
import alertToast from 'Util/ToastifyUtils';
import { isEmail } from 'Validators/Functions/Email';
import { IsValidPhone } from 'Validators/Functions/HumanWritten/Phone';
import { Button, Colors, Display } from 'Views/Components/Button/Button';
import { Flex } from 'Views/Components/Flex/Flex';
import LoaderInsideButton from 'Views/Components/Spinner/LoaderInsideButton';
import { TextInput } from 'Views/Components/New/TextInput';
import { PhoneInputField } from 'Views/Components/New/PhoneInputField';

interface GuestState {
	user: UserEntity;
	errors: Record<string, string | undefined>;
}

interface GuestSetupProps {
	onBack: () => void;
	onSuccess: (user: UserEntity) => void;
}

function GuestSetup({ onBack, onSuccess }: GuestSetupProps) {
	const intl = useIntl();

	const loading = useLocalStore<{ value: boolean }>(() => ({ value: false }));
	const guestState = useLocalStore<GuestState>(() => ({
		user: new UserEntity(),
		errors: {},
	}));

	const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		runInAction(() => {
			guestState.errors = {};

			const { email, firstName, lastName, phone, postcode } = guestState.user;

			if (!email) {
				guestState.errors.email = intl.formatMessage(WLIDS.validation.emailRequired);
			} else if (!isEmail(email)) {
				guestState.errors.email = 'Invalid email address';
			}

			if (appConfigStore.userPhoneEnabled && !IsValidPhone(phone, !appConfigStore.userPhoneRequired)) {
				guestState.errors.phone = 'Invalid phone number';
			}

			if (!firstName) {
				guestState.errors.firstName = intl.formatMessage(WLIDS.validation.required);
			} else if (checkIfInvalidName(firstName)) {
				guestState.errors.firstName = intl.formatMessage(WLIDS.validation.nameInvalid);
			}

			if (!lastName) {
				guestState.errors.lastName = intl.formatMessage(WLIDS.validation.required);
			} else if (checkIfInvalidName(lastName)) {
				guestState.errors.lastName = intl.formatMessage(WLIDS.validation.nameInvalid);
			}

			if (appConfigStore.userPostcodeEnabled && !postcode) {
				guestState.errors.postcode = intl.formatMessage(WLIDS.validation.required);
			}

			if (Object.keys(guestState.errors).length === 0) {
				runInAction(() => {
					loading.value = true;
				});
				guestCreation({ email, firstName, lastName, phone, postcode })
					.then(async data => {
						if (data.user) {
							onSuccess(new UserEntity(data.user));
						}
					})
					.catch(response => {
						if (response?.response?.data?.code === ERROR_CODES.EMAIL_ALREADY_EXISTS) {
							runInAction(() => {
								if (store.isStaff) {
									guestState.errors.email = intl.formatMessage(
										WLIDS.validation.emailAlreadyExistsFromStaff,
									);
								} else {
									guestState.errors.email = intl.formatMessage(WLIDS.validation.emailAlreadyExists);
								}
							});
						} else {
							alertToast('', 'error', 'Guest creation failed');
						}
					})
					.finally(() => {
						runInAction(() => {
							loading.value = false;
						});
					});
			}
		});
	};

	return (
		<form onSubmit={onSubmit} className="guest-setup">
			<Flex direction="col" gap="md">
				<Flex direction="col" gap="xs">
					<Flex className="login-flow__row">
						<TextInput
							className="flex-1"
							label={intl.formatMessage(WLIDS.form.firstName)}
							placeholder={intl.formatMessage(WLIDS.form.firstNamePlaceholder)}
							data-test-id="staff-guest-first-name"
							required
							value={guestState.user.firstName ?? ''}
							error={guestState.errors.firstName}
							onChange={e =>
								runInAction(() => {
									guestState.user.firstName = e.target.value;
									guestState.errors.firstName = undefined;
								})
							}
						/>
						<TextInput
							className="flex-1"
							label={intl.formatMessage(WLIDS.form.lastName)}
							placeholder={intl.formatMessage(WLIDS.form.lastNamePlaceholder)}
							data-test-id="staff-guest-last-name"
							required
							value={guestState.user.lastName ?? ''}
							error={guestState.errors.lastName}
							onChange={e =>
								runInAction(() => {
									guestState.user.lastName = e.target.value;
									guestState.errors.lastName = undefined;
								})
							}
						/>
					</Flex>
					<TextInput
						label={intl.formatMessage(WLIDS.form.email)}
						placeholder={intl.formatMessage(WLIDS.form.emailPlaceholder)}
						autoComplete="username"
						type="email"
						data-test-id="staff-guest-email"
						required
						value={guestState.user.email ?? ''}
						error={guestState.errors.email}
						onChange={e =>
							runInAction(() => {
								guestState.user.email = e.target.value;
								guestState.errors.email = undefined;
							})
						}
					/>
					<Flex className="login-flow__row">
						{appConfigStore.userPhoneEnabled && (
							<PhoneInputField
								className="flex-1"
								model={guestState.user}
								modelProperty="phone"
								label={<FormattedMessage {...WLIDS.form.mobileNumber} />}
								placeholder={intl.formatMessage(WLIDS.form.mobileNumberPlaceholder)}
								required={appConfigStore.userPhoneRequired}
								data-test-id="staff-guest-phone"
								onAfterChange={() =>
									runInAction(() => {
										guestState.errors.phone = undefined;
									})
								}
								error={guestState.errors.phone}
							/>
						)}
						{appConfigStore.userPostcodeEnabled && (
							<TextInput
								className="flex-1"
								label={intl.formatMessage(WLIDS.form.postcode)}
								placeholder={intl.formatMessage(WLIDS.form.postcodePlaceholder)}
								data-test-id="staff-guest-postcode"
								required
								value={guestState.user.postcode ?? ''}
								error={guestState.errors.postcode}
								onChange={e =>
									runInAction(() => {
										guestState.user.postcode = e.target.value;
										guestState.errors.postcode = undefined;
									})
								}
							/>
						)}
					</Flex>
				</Flex>
				<Flex direction="col" gap="xs">
					<Flex gap="xs">
						<Button
							display={Display.Outline}
							colors={Colors.Secondary}
							onClick={onBack}
							newStyles
							buttonProps={{ style: { flex: 2 } }}
						>
							<FormattedMessage {...WLIDS.actions.back} />
						</Button>
						<Button
							type="submit"
							display={Display.Solid}
							colors={Colors.Primary}
							newStyles
							buttonProps={{ style: { flex: 5 } }}
						>
							<FormattedMessage {...WLIDS.actions.continue} />
							{loading.value && <LoaderInsideButton />}
						</Button>
					</Flex>
				</Flex>
			</Flex>
		</form>
	);
}

export default observer(GuestSetup);
